import {
  AuthDto,
  ZohoCreateFolderFnReq,
  ZohoCreateFolderFnRes,
  ZohoGetFileFnReq,
  ZohoGetFileFnRes,
  ZohoGetThumbnailsFnReq,
  ZohoGetThumbnailsFnRes,
  ZohoListFolderFnReq,
  ZohoListFolderFnRes,
  ZohoListFolderItem,
  ZohoListTeamFolderItem,
  ZohoListTeamFoldersFnReq,
  ZohoListTeamFoldersFnRes,
  ZohoMoveFilesFnReq,
  ZohoMoveFilesFnRes,
} from '@uploadcam/common';
import { ZohoFileResource } from './zoho.item';

interface ZohoEndpoint {
  apiDomain: string;
  headers: Record<string, string>;
}

/**
 * Request shape for {@link ZohoApiClient.renameFolder}. Kept local to the
 * frontend (there is no backend callable counterpart for this operation).
 */
export interface ZohoRenameFolderReq {
  auth: AuthDto;
  fileId: string;
  name: string;
}

/**
 * Browser-side Zoho WorkDrive client. Mirrors the contract of the deprecated
 * `zoho*` Firebase callable functions, but issues `fetch` calls directly.
 *
 * Requires Zoho's "client-based application" with the app's origin configured
 * as a JavaScript Domain so cross-origin requests pass.
 */
export class ZohoApiClient {
  public async createFolder(req: ZohoCreateFolderFnReq): Promise<ZohoCreateFolderFnRes> {
    const ep = this.endpoint(req.auth);
    const url = `${ep.apiDomain}/workdrive/api/v1/files`;

    const json = await this.fetchJson(url, {
      method: 'POST',
      headers: {
        ...ep.headers,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          type: 'files',
          attributes: { name: req.name, parent_id: req.parentId },
        },
      }),
    });

    const data = json?.data;
    if (!data?.id || !data?.attributes) {
      throw new Error('Zoho create folder response missing id or attributes');
    }

    return {
      id: data.id,
      attributes: {
        name: data.attributes.name ?? req.name,
        type: data.attributes.type ?? 'folder',
        modified_time: data.attributes.modified_time,
        parent_id: data.attributes.parent_id ?? req.parentId,
      },
    };
  }

  public async renameFolder(req: ZohoRenameFolderReq): Promise<ZohoFileResource> {
    const ep = this.endpoint(req.auth);
    const url = `${ep.apiDomain}/workdrive/api/v1/files/${req.fileId}`;

    const json = await this.fetchJson(url, {
      method: 'PATCH',
      headers: {
        ...ep.headers,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          id: req.fileId,
          type: 'files',
          attributes: { name: req.name },
        },
      }),
    });

    const data = json?.data;
    if (!data?.id || !data?.attributes) {
      throw new Error('Zoho rename folder response missing id or attributes');
    }

    return {
      id: data.id,
      attributes: {
        name: data.attributes.name ?? req.name,
        type: data.attributes.type ?? 'folder',
        modified_time: data.attributes.modified_time,
        parent_id: data.attributes.parent_id,
      },
    };
  }

  public async listFolder(req: ZohoListFolderFnReq): Promise<ZohoListFolderFnRes> {
    const ep = this.endpoint(req.auth);
    const offset = req.pageOffset ?? 0;
    const limit = req.pageLimit ?? 50;

    const params = [`page%5Blimit%5D=${limit}`, `page%5Boffset%5D=${offset}`];
    if (req.onlyFolders) {
      params.push('filter%5Btype%5D=folder');
    }

    const json = await this.zohoFetch(ep, `/files/${req.folderId}/files?${params.join('&')}`);

    const items: ZohoListFolderItem[] = json.data.map(
      (resource: { id: string; attributes: Record<string, any> }) => ({
        id: resource.id,
        name: resource.attributes['name'],
        type: resource.attributes['type'],
        modified_time: resource.attributes['modified_time'],
        parent_id: resource.attributes['parent_id'],
      }),
    );

    const hasMore = items.length === limit;
    const nextOffset = hasMore ? offset + items.length : undefined;
    return { items, hasMore, nextOffset };
  }

  public async listTeamFolders(req: ZohoListTeamFoldersFnReq): Promise<ZohoListTeamFoldersFnRes> {
    const ep = this.endpoint(req.auth);

    // /users/me returns a single object — extract the preferred team id
    const userRes = await this.zohoFetch(ep, '/users/me');
    const teamId = userRes?.data?.attributes?.['preferred_team_id'];
    if (!teamId) {
      throw new Error('User has no preferred_team_id');
    }

    const foldersRes = await this.zohoFetch(ep, `/teams/${teamId}/teamfolders`);

    const folders: ZohoListTeamFolderItem[] = foldersRes.data.map(
      (resource: { id: string; attributes: Record<string, any> }) => ({
        id: resource.id,
        name: resource.attributes['name'],
        type: resource.attributes['type'],
        modified_time: resource.attributes['modified_time'],
        parent_id: resource.attributes['parent_id'],
      }),
    );

    return { folders };
  }

  public async getFile(req: ZohoGetFileFnReq): Promise<ZohoGetFileFnRes> {
    const ep = this.endpoint(req.auth);
    const json = await this.zohoFetch(ep, `/files/${req.fileId}`);
    return { id: json.data.id, attributes: json.data.attributes };
  }

  public async getThumbnails(req: ZohoGetThumbnailsFnReq): Promise<ZohoGetThumbnailsFnRes> {
    if (!Array.isArray(req.fileIds) || req.fileIds.length === 0) {
      return { urls: {} };
    }

    const ep = this.endpoint(req.auth);
    const attr = req.type === 'thumbnail' ? 'thumbnail_url' : 'download_url';

    const urls: Record<string, string | undefined> = {};
    const settled = await Promise.allSettled(
      req.fileIds.map(async (fileId) => {
        const json = await this.zohoFetch(ep, `/files/${fileId}`);
        return { fileId, url: json.data.attributes[attr] ?? undefined };
      }),
    );
    for (const result of settled) {
      if (result.status === 'fulfilled') {
        urls[result.value.fileId] = result.value.url;
      }
    }

    return { urls };
  }

  public async moveFiles(req: ZohoMoveFilesFnReq): Promise<ZohoMoveFilesFnRes> {
    if (!Array.isArray(req.fileIds) || req.fileIds.length === 0) {
      return { results: {} };
    }

    const ep = this.endpoint(req.auth);
    const url = `${ep.apiDomain}/workdrive/api/v1/files`;
    const body = {
      data: req.fileIds.map((id) => ({
        id,
        type: 'files',
        attributes: { parent_id: req.targetFolderId },
      })),
    };

    const json = await this.fetchJson(url, {
      method: 'PATCH',
      headers: {
        ...ep.headers,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.api+json',
      },
      body: JSON.stringify(body),
    });

    const movedIds = new Set<string>(
      Array.isArray(json?.data) ? json.data.map((entry: { id: string }) => entry.id) : [],
    );

    const results: Record<string, boolean> = {};
    for (const id of req.fileIds) {
      results[id] = movedIds.has(id);
    }
    return { results };
  }

  private endpoint(auth: AuthDto): ZohoEndpoint {
    const apiDomain = auth.metadata?.['api_domain'];
    if (typeof apiDomain !== 'string' || apiDomain.length === 0) {
      throw new Error('Zoho api_domain is required');
    }
    return {
      apiDomain,
      headers: {
        Authorization: `Zoho-oauthtoken ${auth.accessToken}`,
      },
    };
  }

  private async zohoFetch(ep: ZohoEndpoint, path: string): Promise<any> {
    const url = `${ep.apiDomain}/workdrive/api/v1${path}`;
    return this.fetchJson(url, { headers: ep.headers });
  }

  private async fetchJson(url: string, init: RequestInit): Promise<any> {
    const res = await fetch(url, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Zoho API error ${res.status}: ${text}`);
    }
    return res.json();
  }
}
